import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { Subscription, catchError, of, tap } from 'rxjs'

import { HlmDialogService } from '@yoshu-lib/ui/dialog/src'

import { AgentEvent } from '@yoshu-lib/models/agent-event.model'
import { ChatMessage, NewMessage, Source } from '@yoshu-lib/models/chat-message.model'

import { ChatsService } from '@yoshu-lib/core/api/chats/chats.service'
import {
  ProtocolSession,
  ProtocolSessionCreatePayload,
  ProtocolSessionHistoryItem,
  ProtocolStepPayload
} from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'
import { ProtocolSessionsService } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.service'
import { ProtocolStep } from '@yoshu-lib/core/api/protocols/protocols.model'
import { ChatStore } from '@yoshu-lib/core/chat/chat.store'
import { SocketService } from '@yoshu-lib/core/socket/socket.service'
import { ToastService } from '@yoshu-lib/core/toast/toast.service'

import {
  GROUP_STYLE_MARKER_PREFIX,
  GroupStyle,
  WorkflowEndConfig
} from '@yoshu-lib/features/admin/pages/protocols/protocol-revisions/workflow-step-config/workflow-step-config.model'
import {
  UserFeedback,
  UserFeedbackAnswer
} from '@yoshu-lib/features/interaction/chat-view/user-feedback/user-feedback.component'

import { localizeStep } from '@yoshu-lib/utils/localize-step'

import { MissingFailureModeFeedbackDialogComponent } from '@yoshu-lib/components/missing-failure-mode-feedback/missing-failure-mode-feedback-dialog.component'
import {
  SessionFeedbackDialogComponent,
  SessionFeedbackDialogContext
} from '@yoshu-lib/components/session-feedback/session-feedback-dialog.component'
import { SessionFeedbackQuestion } from '@yoshu-lib/components/session-feedback/session-feedback-question.model'
import { StepFeedbackDialogComponent } from '@yoshu-lib/components/step-feedback/step-feedback-dialog.component'

import { TranslateService } from '@ngx-translate/core'
import { LocaleService } from '@yoshu-lib/services/locale.service'

import { renderJinja } from './jinja-renderer'
import {
  PROTOCOL_EVENTS,
  ProtocolHelperOpenedEvent,
  StepModeGatewayService,
  ProtocolNameSource
} from './step-mode-gateway.service'
import {
  StepOption,
  buildActionButtons,
  normalizeStepOptions,
  readSavedInputValue,
  resolveCurrentInputValue,
  findExitEndStep,
  hasSubmittedFeedback,
  protocolStepToPayload,
  resolveEndFeedbackQuestions,
  shouldOpenFeedbackDialog,
  updateContextForInput,
  updateStepHistory,
  FM_NOT_LISTED_TRIGGER
} from './step-mode.utils'

interface ProtocolHelperItem {
  id: string
  label?: string
  icon?: string
  chat_id?: string | null
  available: boolean
  unavailable_reason?: string | null
}

interface ProtocolHelperRegistryItem extends ProtocolHelperItem {
  stepKey: string
}

type HelperChatStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface AgentCacheEntry {
  output: string
  thoughts: string
  sources: Source[]
  toolCalls: ChatMessage[]
  messageId: string
  chatId: string | null
}

export interface ReviewAgentData {
  output: string
  thoughts: string
  sources: Source[]
  toolCalls: ChatMessage[]
  messageId: string
  messageStatus: ChatMessage['status']
}

@Injectable({ providedIn: 'root' })
export class StepModeFacadeService {
  private readonly sessionsService = inject(ProtocolSessionsService)
  private readonly gateway = inject(StepModeGatewayService)
  private readonly chatsService = inject(ChatsService)
  private readonly chatStore = inject(ChatStore)
  private readonly socket = inject(SocketService)
  private readonly toast = inject(ToastService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly dialogService = inject(HlmDialogService)
  private readonly localeService = inject(LocaleService)
  private readonly translate = inject(TranslateService)

  private static readonly AGENT_TIMEOUT_MS = 5 * 60_000

  private protocolSubscribedFor: string | null = null
  private protocolSubscriptions = new Subscription()
  private chatSubscribedFor: string | null = null
  private chatSubscriptions = new Subscription()
  private helperSubscriptions = new Subscription()
  private lastStepId: string | null = null
  /** Prevents stacking SessionFeedbackDialog from loadSession + markSessionCompleted. */
  private feedbackDialogOpen = false
  /** Exit click held until feedback closes — WS transition not sent yet. */
  private pendingExit: { trigger: string; value?: unknown } | null = null
  /** After deferred exit, complete the session when the END step arrives. */
  private completeWhenEndArrives = false
  private feedbackAlreadyToasted = false
  private agentTimeoutId: ReturnType<typeof setTimeout> | null = null
  private pendingHelperMessage: string | null = null
  private readonly helperJoinedChats = new Set<string>()
  private readonly agentOutputCache = new Map<string, AgentCacheEntry>()
  readonly sessionId = signal<string | null>(null)
  readonly sessionDetails = signal<ProtocolSession | null>(null)
  readonly currentStep = signal<ProtocolStepPayload | null>(null)
  readonly workflow = signal<string | null>(null)

  readonly isLoading = signal<boolean>(true)
  readonly isClosed = signal<boolean>(false)
  readonly agentOutput = signal<string>('')
  readonly agentThoughts = signal<string>('')
  readonly agentSources = signal<Source[]>([])
  readonly agentToolCalls = signal<ChatMessage[]>([])
  readonly agentMessageId = signal<string>('')
  readonly agentError = signal<string | null>(null)
  // Structured failure metadata for AgentErrorEvent. Set in tandem with
  // `agentError` when the backend sends a typed reason (see issue #1337).
  // The UI uses these to pick a localized message rather than displaying the
  // English `agentError` fallback string.
  readonly agentErrorReason = signal<string | null>(null)
  readonly agentErrorDomainType = signal<string | null>(null)
  readonly isAgentStreaming = signal<boolean>(false)
  readonly stepHistory = signal<ProtocolSession['step_history']>([])
  readonly localContext = signal<Record<string, unknown>>({})
  readonly helpers = signal<ProtocolHelperItem[]>([])
  readonly helperChats = signal<Record<string, string>>({})
  readonly helperRegistry = signal<Record<string, ProtocolHelperRegistryItem>>({})
  readonly activeHelperKey = signal<string | null>(null)
  readonly helperChatStatus = signal<Record<string, HelperChatStatus>>({})
  readonly reviewAgentOutputs = signal<Record<string, ReviewAgentData>>({})
  private readonly protocolNameOverride = signal<string | null>(null)
  readonly staleTestSession = signal(false)

  readonly isTestSession = computed(() => {
    const revision = this.sessionDetails()?.revision
    return typeof revision === 'object' && !revision.is_published
  })

  readonly testDraftVersion = computed(() => {
    const revision = this.sessionDetails()?.revision
    return typeof revision === 'object' ? revision.version : null
  })

  /**
   * Step lookups for the closed-session recap.
   *
   * `getReviewInputValue` / `getReviewInputOptions` are called once per step by
   * the recap, so scanning `revision.steps` and `stepHistory` inside them made
   * the rebuild grow quadratically with step count. Building the indexes once
   * per revision / history / language change keeps it linear.
   *
   * Both maps keep the FIRST entry for a given id, matching the `find` /
   * `findIndex` semantics they replace.
   */
  private readonly revisionStepById = computed(() => {
    const revision = this.sessionDetails()?.revision
    const byId = new Map<string, ProtocolStep>()
    if (!revision || typeof revision === 'string') {
      return byId
    }

    for (const step of revision.steps ?? []) {
      if (!byId.has(step.id)) {
        byId.set(step.id, step)
      }
    }
    return byId
  })

  /** Localized config of every `input` step, keyed by step id. */
  private readonly localizedInputConfigById = computed(() => {
    const lang = this.localeService.currentLang()
    const byId = new Map<string, Record<string, unknown> | null>()
    for (const [id, step] of this.revisionStepById()) {
      if (step.step_type !== 'input') {
        continue
      }
      const localized = localizeStep(step as ProtocolStepPayload, lang)
      byId.set(id, (localized?.config as Record<string, unknown> | null) ?? null)
    }
    return byId
  })

  /** First step-history entry per step id. */
  private readonly historyEntryByStepId = computed(() => {
    const byId = new Map<string, ProtocolSessionHistoryItem>()
    for (const entry of this.stepHistory() ?? []) {
      if (!byId.has(entry.step_id)) {
        byId.set(entry.step_id, entry)
      }
    }
    return byId
  })

  constructor() {
    this.gateway
      .listenSessionCreated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!event?.session_id) {
          return
        }

        const name = typeof event.protocol_name === 'string' ? event.protocol_name.trim() : ''
        if (name && this.sessionId() === event.session_id) {
          this.setProtocolNameOverride(
            this.localizedProtocolName({ name, translations: event.protocol_translations ?? null })
          )
        }
      })
  }

  readonly localizedCurrentStep = computed(() =>
    localizeStep(this.currentStep(), this.localeService.currentLang())
  )

  readonly availableTransitions = computed(
    () => this.currentStep()?.available_transitions?.map((t) => t.trigger) ?? []
  )

  readonly hasEarlyExit = computed(() => this.availableTransitions().includes('problem_resolved'))

  readonly inputOptions = computed<StepOption[]>(() => {
    const step = this.localizedCurrentStep()
    if (!step || !step.config || typeof step.config !== 'object') {
      return []
    }

    const options = step.config['options']
    if (!Array.isArray(options)) {
      return []
    }

    return normalizeStepOptions(options)
  })

  readonly inputTemplate = computed<string | null>(() => {
    const step = this.localizedCurrentStep()
    if (!step || !step.config || typeof step.config !== 'object') return null
    const template = (step.config as Record<string, unknown>)['template']
    return typeof template === 'string' && template.length > 0 ? template : null
  })

  readonly inputTemplateData = computed<unknown | null>(() => {
    const step = this.localizedCurrentStep()
    if (!step || !step.config || typeof step.config !== 'object') return null
    const data = (step.config as Record<string, unknown>)['data']
    return data ?? null
  })

  readonly inputGroupStyle = computed<GroupStyle>(() => {
    const step = this.localizedCurrentStep()
    if (!step || !step.config || typeof step.config !== 'object') {
      return 'default'
    }
    const options = step.config['options']
    if (!Array.isArray(options)) {
      return 'default'
    }
    for (const option of options) {
      if (typeof option === 'string' && option.startsWith(GROUP_STYLE_MARKER_PREFIX)) {
        return option.slice(GROUP_STYLE_MARKER_PREFIX.length) as GroupStyle
      }
    }
    return 'default'
  })

  readonly actionButtons = computed(() =>
    buildActionButtons(this.localizedCurrentStep(), this.availableTransitions())
  )

  readonly activeHelperChatId = computed(() => {
    const key = this.activeHelperKey()
    if (!key) {
      return null
    }

    return this.helperChats()[key] ?? null
  })

  readonly activeHelperMeta = computed(() => {
    const key = this.activeHelperKey()
    if (!key) {
      return null
    }

    return this.helperRegistry()[key] ?? null
  })

  readonly currentStepKey = computed(() => {
    const step = this.currentStep()
    return step ? this.resolveHelperStepKey(step) : null
  })

  readonly activeHelperIdForStep = computed(() => {
    const stepKey = this.currentStepKey()
    const activeKey = this.activeHelperKey()

    if (!stepKey || !activeKey) {
      return null
    }

    const prefix = `${stepKey}.`
    if (!activeKey.startsWith(prefix)) {
      return null
    }

    return activeKey.slice(prefix.length)
  })

  readonly helperMessages = computed(() => {
    const chatId = this.activeHelperChatId()
    return chatId ? this.chatStore.messagesForChat()(chatId) : []
  })

  readonly hasHelpersOnStep = computed(() => this.helpers().length > 0)
  readonly canSendHelperMessage = computed(() => {
    return Boolean(
      this.activeHelperKey() && this.hasHelpersOnStep() && this.isActiveHelperForCurrentStep()
    )
  })

  readonly isHelperChatLoading = computed(() => {
    const chatId = this.activeHelperChatId()
    if (!chatId) {
      return false
    }

    return this.helperChatStatus()[chatId] === 'loading'
  })

  readonly isHelperChatError = computed(() => {
    const chatId = this.activeHelperChatId()
    if (!chatId) {
      return false
    }

    return this.helperChatStatus()[chatId] === 'error'
  })

  readonly hasHelperChats = computed(() => Object.keys(this.helperChats()).length > 0)

  readonly feedback = computed<UserFeedback | null>(() => {
    const session = this.sessionDetails()
    if (!session) {
      return null
    }

    const { user_feedback, feedback_rating } = session
    const answers = this.summarisedFeedbackAnswers()
    if (user_feedback != null || feedback_rating != null || answers.length) {
      return { message: user_feedback ?? '', rating: feedback_rating ?? null, answers }
    }

    return null
  })

  /**
   * Pair the session's stored `feedback_answers` with the END step's declared
   * questions so the summary card can show what was actually asked and
   * answered, rather than just a rating.
   *
   * Questions drive the order (the order the operator saw), and answers that
   * are absent, cleared, or belong to a follow-up that stayed hidden are
   * skipped. A choice renders its option's label: the stored value is an id or
   * a code and means nothing on its own.
   *
   * Question definitions fall back through current step → session.current_step
   * → revision END steps: a completed session is loaded over REST and never
   * gets a `protocol.step` frame, so the live current-step config alone is not
   * enough.
   */
  private summarisedFeedbackAnswers(): UserFeedbackAnswer[] {
    const stored = this.sessionDetails()?.feedback_answers
    if (!stored || !Object.keys(stored).length) {
      return []
    }

    const revision = this.sessionDetails()?.revision
    const revisionSteps = revision && typeof revision !== 'string' ? (revision.steps ?? null) : null
    const lang = this.localeService.currentLang()
    const sessionStep = this.sessionDetails()?.current_step
    const questions = resolveEndFeedbackQuestions({
      currentStep: this.localizedCurrentStep(),
      sessionCurrentStep: sessionStep
        ? localizeStep(protocolStepToPayload(sessionStep), lang)
        : null,
      revisionSteps: (revisionSteps ?? [])
        .filter((step) => step.step_type === 'end')
        .map((step) => localizeStep(protocolStepToPayload(step), lang))
    })

    const summarised: UserFeedbackAnswer[] = []

    // Pack E: dedicated missing-FM comment is not on the END questionnaire
    // (end-fm-not-listed has show_feedback=false) — still show it on the recap.
    const fmComment = stored['fm_not_listed_comment']
    if (typeof fmComment === 'string' && fmComment.trim()) {
      summarised.push({
        label: this.translate.instant('step_mode.failure_mode.review_comment_label'),
        value: fmComment.trim()
      })
    }

    for (const question of questions) {
      const answer = stored[question.key]
      if (answer === undefined || answer === null || answer === '') {
        continue
      }
      summarised.push({
        label: question.label ?? question.key,
        value: this.feedbackAnswerLabel(question, answer)
      })
    }
    return summarised
  }

  private feedbackAnswerLabel(
    question: SessionFeedbackQuestion,
    answer: string | boolean | number
  ): string {
    if (typeof answer === 'boolean') {
      const custom = answer ? question.yes_label : question.no_label
      return custom ?? this.translate.instant(answer ? 'buttons.yes' : 'buttons.no')
    }
    if (question.type === 'choice') {
      const option = (question.options ?? []).find((o) => o.value === answer)
      return option?.label ?? String(answer)
    }
    return String(answer)
  }

  readonly currentInputValue = computed(() => {
    const step = this.currentStep()
    if (!step || step.step_type !== 'input') {
      return null
    }

    const config =
      step.config && typeof step.config === 'object' ? (step.config as Record<string, unknown>) : {}

    // The server's own `current_value` when the payload carries it (#3070),
    // else the same reader the closed-session recap uses over the local
    // snapshot. An answer is persisted split across `saves_to`, `ok_saves_to`
    // and `followup.saves_to`; a reader that knows only the first sees a partial
    // answer, renders the gaps, then re-submits them over the halves it never
    // saw. The two readers must not diverge.
    return resolveCurrentInputValue(config, this.localContext())
  })

  initRoutingContext(route: ActivatedRoute) {
    const sessionId = route.snapshot.paramMap.get('sessionId')
    if (sessionId) {
      this.sessionId.set(sessionId)
    }

    const workflow = route.snapshot.parent?.data?.['workflow'] ?? route.snapshot.data['workflow']
    if (workflow) {
      this.workflow.set(workflow)
    }
  }

  loadSession(sessionId: string) {
    this.isLoading.set(true)
    this.sessionId.set(sessionId)
    // Cancel any pending feedback open from a previous session load.
    this.feedbackDialogOpen = false

    return this.sessionsService.getSessionById(sessionId).pipe(
      tap((session) => {
        this.sessionDetails.set(session)
        const history = session.step_history ?? []
        this.stepHistory.set(history)
        const lastSnapshot =
          history.length > 0 ? history[history.length - 1].context_snapshot : null
        this.localContext.set(lastSnapshot ?? session.context ?? {})
        this.applyProtocolNameOverride()
        this.applyProtocolNameFromCache(sessionId)
        this.lastStepId = session.current_step?.id ?? null
        // Hydrate the live current-step signal from REST. Completed sessions
        // never get a `protocol.step` frame (resume is skipped), so without
        // this the END questionnaire has no question defs and the feedback
        // card hides the operator's answers.
        if (session.current_step && session.current_step.step_type !== 'compute') {
          this.currentStep.set(protocolStepToPayload(session.current_step))
        }
        if (session.status === 'completed') {
          this.isClosed.set(true)
          // Drop the auto-resume frame: completed sessions resume read-only.
          // (subscribeProtocol may have registered one before this REST call
          // resolved.)
          this.socket.setResumePayload('protocol', null)
          if (session.helper_chats) {
            this.helperChats.set(session.helper_chats as Record<string, string>)
          }
          this.loadAllStepChatOutputs()
        } else if (session.current_step?.step_type === 'end' && !hasSubmittedFeedback(session)) {
          // Abandoned mid-END (e.g. older builds): finish feedback then complete.
          this.maybeOpenFeedbackDialog()
        }
      }),
      tap(() => this.isLoading.set(false)),
      catchError((err) => {
        console.error('Failed to load protocol session', err)
        this.isLoading.set(false)
        this.toast.error(this.translate.instant('step_mode.errors.load_session_failed'))
        return of(void 0)
      })
    )
  }

  subscribeProtocol(sessionId: string) {
    if (this.protocolSubscribedFor === sessionId) {
      return
    }

    this.resetProtocolSubscriptions()
    this.resetHelperSubscriptions()
    this.protocolSubscribedFor = sessionId
    this.sessionId.set(sessionId)

    // Register a protocol.start{session_id} frame so the socket layer auto-resumes
    // this session on every reconnect. Skipped for completed sessions — the
    // backend rejects resume of completed sessions with an error.
    if (!this.isClosed()) {
      this.socket.setResumePayload('protocol', {
        event: PROTOCOL_EVENTS.START,
        data: { session_id: sessionId }
      })
    }

    // Connection-loss UX is owned by the global ConnectionBanner subscribed to
    // socket.connectionStatus(); the socket layer reconnects on its own with
    // unbounded exponential backoff + heartbeat, replays the resume frame, and
    // restores the session without user intervention.

    this.initHelperSubscriptions()

    this.protocolSubscriptions.add(
      this.gateway.listenStep().subscribe((step) => {
        this.cacheAgentOutput()
        this.currentStep.set(step)
        this.isLoading.set(false)
        if (step.step_type === 'end') {
          if (this.completeWhenEndArrives) {
            this.completeWhenEndArrives = false
            this.completeSessionAfterFeedback({
              feedbackAlreadyToasted: this.feedbackAlreadyToasted
            })
            this.feedbackAlreadyToasted = false
          } else {
            // Landed on END without a deferred exit (legacy / compute-only path).
            this.maybeOpenFeedbackDialog()
          }
        }
        this.updateStepHistory(step)
        this.updateHelpersForStep(this.localizedCurrentStep() ?? step)
        this.resetAgentState(step)
        this.subscribeChatForStep(step)
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenEnd().subscribe(() => {
        // Legacy / other clients may still emit protocol.end; treat it as done.
        this.markSessionCompleted()
        this.isLoading.set(false)
        this.toast.success(this.translate.instant('step_mode.toasts.protocol_completed'))
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenError().subscribe((err) => {
        if (this.isTestSession() && err?.message?.includes('no current step')) {
          this.staleTestSession.set(true)
          this.isLoading.set(false)
          return
        }
        this.toast.error(err?.message || this.translate.instant('step_mode.errors.protocol_error'))
        this.isLoading.set(false)
      })
    )

    this.protocolSubscriptions.add(
      this.gateway.listenHelperOpened().subscribe((event) => {
        this.handleHelperOpened(event)
      })
    )
  }

  sendTransition(trigger: string, value?: unknown) {
    if (trigger === 'next' && typeof value !== 'undefined') {
      this.localContext.update((current) =>
        updateContextForInput({ step: this.currentStep(), value, context: current })
      )
    }

    // Gate exits that end the protocol: open feedback first, fire the WS
    // transition only when the dialog closes. Refresh before that leaves the
    // operator on the same step so they must click again.
    const endStep = this.resolveExitEndStep(trigger)
    if (endStep) {
      this.beginDeferredExit(trigger, value, endStep)
      return
    }

    this.gateway.transition(trigger, value)
    this.isLoading.set(true)
  }

  /**
   * Pack E: dedicated missing-FM form → PATCH feedback → transition to
   * ``end-fm-not-listed`` via ``action:fm_not_listed``. Does not open the generic
   * END questionnaire (that exit's ``show_feedback`` is false).
   */
  openMissingFailureModeFeedback() {
    const sessionId = this.sessionId()
    if (!sessionId || this.feedbackDialogOpen) {
      return
    }

    this.feedbackDialogOpen = true
    let dialogRef
    try {
      dialogRef = this.dialogService.open(MissingFailureModeFeedbackDialogComponent, {
        // $hideCloseButton: the default X does not wire to cancel; Annuler is the exit.
        context: { sessionId, $hideCloseButton: true },
        contentClass: '!w-[92vw] !max-w-lg',
        disableClose: true,
        closeOnBackdropClick: false,
        closeOnOutsidePointerEvents: false
      })
    } catch {
      // Synchronous open failure must not leave the re-entrancy gate stuck shut.
      this.feedbackDialogOpen = false
      return
    }

    dialogRef.closed$.subscribe((result?: boolean | ProtocolSession) => {
      this.feedbackDialogOpen = false
      const submitted = !!result && typeof result === 'object' && 'id' in result
      if (!submitted) {
        return
      }
      this.sessionDetails.set(result)
      this.toast.success(this.translate.instant('step_mode.toasts.feedback_submitted'))
      this.feedbackAlreadyToasted = true
      this.completeWhenEndArrives = true
      this.gateway.transition(FM_NOT_LISTED_TRIGGER)
      this.isLoading.set(true)
    })
  }

  private resolveExitEndStep(trigger: string): ProtocolStep | null {
    const revision = this.sessionDetails()?.revision
    const steps = revision && typeof revision !== 'string' ? revision.steps : null
    const current = this.currentStep()
    return findExitEndStep({
      steps,
      fromStepIdOrSlug: current?.id ?? current?.slug ?? null,
      trigger,
      context: this.localContext()
    })
  }

  private beginDeferredExit(trigger: string, value: unknown, endStep: ProtocolStep) {
    // Same re-entrancy gate as maybeOpenFeedbackDialog — a second exit click
    // (or loadSession opening feedback concurrently) must not stack dialogs or
    // overwrite pendingExit.
    if (this.feedbackDialogOpen) {
      return
    }

    const sessionId = this.sessionId()
    if (!sessionId) {
      return
    }

    // Same overlay maybeOpenFeedbackDialog gets via localizedCurrentStep.
    // Without it the early-exit path shows canonical English labels under
    // the French dialog chrome — FR already lives on endStep.translations.
    const localized = localizeStep(endStep, this.localeService.currentLang())
    const config =
      localized.config && typeof localized.config === 'object'
        ? (localized.config as WorkflowEndConfig)
        : null

    const syntheticEnd: Pick<ProtocolStepPayload, 'step_type' | 'config'> = {
      step_type: 'end',
      config: (config ?? { show_feedback: true }) as Record<string, unknown>
    }

    if (!shouldOpenFeedbackDialog(syntheticEnd, this.sessionDetails())) {
      this.completeWhenEndArrives = true
      this.gateway.transition(trigger, value)
      this.isLoading.set(true)
      return
    }

    this.pendingExit = { trigger, value }
    this.feedbackDialogOpen = true
    this.openSessionFeedbackDialog({
      sessionId,
      prompt: config?.feedback_prompt,
      ratingEnabled: config?.feedback_rating_enabled ?? true,
      commentEnabled: config?.feedback_comment_enabled ?? true,
      questions: config?.feedback_questions as SessionFeedbackQuestion[] | undefined
    })
  }

  goBack() {
    this.gateway.goBack()
    this.isLoading.set(true)
  }

  jumpToStep(stepId: string) {
    this.gateway.jumpToStep(stepId)
    this.isLoading.set(true)
  }

  resumeSession(sessionId: string) {
    this.isLoading.set(true)
    this.subscribeProtocol(sessionId)
    this.gateway.resumeSession(sessionId)
  }

  resetState() {
    this.resetProtocolSubscriptions()
    this.resetChatSubscriptions()
    this.resetHelperSubscriptions()
    this.clearAgentTimeout()
    // Drop any pending session-bound state from the socket layer (resume frame
    // and queued frames that belong to the session we're leaving).
    this.socket.setResumePayload('protocol', null)
    this.pendingHelperMessage = null
    this.pendingExit = null
    this.completeWhenEndArrives = false
    this.feedbackAlreadyToasted = false
    this.feedbackDialogOpen = false
    this.lastStepId = null
    this.sessionId.set(null)
    this.sessionDetails.set(null)
    this.currentStep.set(null)
    this.workflow.set(null)
    this.isLoading.set(true)
    this.isClosed.set(false)
    this.agentOutput.set('')
    this.agentThoughts.set('')
    this.agentSources.set([])
    this.agentToolCalls.set([])
    this.agentMessageId.set('')
    this.agentError.set(null)
    this.agentErrorReason.set(null)
    this.agentErrorDomainType.set(null)
    this.isAgentStreaming.set(false)
    this.stepHistory.set([])
    this.localContext.set({})
    this.helpers.set([])
    this.helperChats.set({})
    this.helperRegistry.set({})
    this.activeHelperKey.set(null)
    this.helperChatStatus.set({})
    this.reviewAgentOutputs.set({})
    this.agentOutputCache.clear()
    this.protocolNameOverride.set(null)
    this.staleTestSession.set(false)
  }

  setProtocolNameOverride(name: string | null) {
    const trimmed = typeof name === 'string' ? name.trim() : ''
    if (!trimmed) {
      return
    }

    this.protocolNameOverride.set(trimmed)
    this.applyProtocolNameOverride()
  }

  openHelper(helperId: string) {
    if (!helperId) {
      return
    }

    const helper = this.helpers().find((h) => h.id === helperId)
    if (helper && helper.available === false) {
      return
    }

    this.gateway.openHelper(helperId)
  }

  selectHelper(helperId: string, reviewStepKey?: string) {
    const helper = this.helpers().find((h) => h.id === helperId)
    if (helper && helper.available === false) {
      return
    }

    const stepKey =
      reviewStepKey ?? (this.currentStep() ? this.resolveHelperStepKey(this.currentStep()!) : null)
    if (!stepKey) {
      return
    }

    const helperKey = this.buildHelperKey(stepKey, helperId)
    this.activeHelperKey.set(helperKey)
    this.pendingHelperMessage = null

    const chatId = this.helperChats()[helperKey]
    if (chatId) {
      this.ensureHelperChatLoaded(chatId)
    }
  }

  loadAllStepChatOutputs() {
    const session = this.sessionDetails()
    if (!session) {
      return
    }

    const revision = session.revision
    if (!revision || typeof revision === 'string') {
      return
    }

    // Use the final session context — it has all saves_to values from completed steps.
    const context = (this.localContext() ?? session.context ?? {}) as Record<string, unknown>
    const outputs: Record<string, ReviewAgentData> = {}

    for (const step of revision.steps ?? []) {
      if (step.step_type !== 'ai') {
        continue
      }

      const slug = step.slug ?? step.id
      if (!slug) {
        continue
      }

      // Read the saved output from context via saves_to.
      const config = step.config as Record<string, unknown> | null
      const savesTo = typeof config?.['saves_to'] === 'string' ? config['saves_to'] : ''
      if (savesTo) {
        const path = savesTo.startsWith('context.') ? savesTo.slice(8) : savesTo
        const value = this.getValueAtPath(context, path)
        if (typeof value === 'string' && value.trim()) {
          outputs[slug] = {
            output: value,
            thoughts: '',
            sources: [],
            toolCalls: [],
            messageId: '',
            messageStatus: 'done'
          }
          continue
        }
      }

      // Fallback: try loading from chat messages.
      const stepChats = session.step_chats as Record<string, string> | undefined
      const chatId = stepChats?.[slug]
      if (typeof chatId === 'string') {
        const capturedSlug = slug
        this.chatsService.getMessages(chatId).subscribe({
          next: (messages) => {
            // Don't overwrite a value that was already set (e.g. from cache).
            if (this.reviewAgentOutputs()[capturedSlug]) {
              return
            }
            const assistantMsg = [...messages].reverse().find((m) => m.role === 'assistant')
            const text = assistantMsg ? this.getTextFromMessage(assistantMsg) : ''
            const toolMessages = messages.filter((m) => m.role === 'tool')
            this.reviewAgentOutputs.update((map) => ({
              ...map,
              [capturedSlug]: {
                output: text,
                thoughts: assistantMsg?.thoughts ?? '',
                sources: assistantMsg?.sources ?? [],
                toolCalls: toolMessages,
                messageId: assistantMsg?.id ?? '',
                messageStatus: 'done'
              }
            }))
          },
          error: () => {
            if (!this.reviewAgentOutputs()[capturedSlug]) {
              this.reviewAgentOutputs.update((map) => ({
                ...map,
                [capturedSlug]: {
                  output: '',
                  thoughts: '',
                  sources: [],
                  toolCalls: [],
                  messageId: '',
                  messageStatus: 'done'
                }
              }))
            }
          }
        })
      } else {
        outputs[slug] = {
          output: '',
          thoughts: '',
          sources: [],
          toolCalls: [],
          messageId: '',
          messageStatus: 'done'
        }
      }
    }

    // Merge into existing values — never overwrite non-empty entries (e.g. from cache).
    if (Object.keys(outputs).length) {
      this.reviewAgentOutputs.update((current) => {
        const merged = { ...current }
        for (const [key, value] of Object.entries(outputs)) {
          if (!merged[key]) {
            merged[key] = value
          }
        }
        return merged
      })
    }
  }

  loadReviewHelpers(step: ProtocolStep | null) {
    if (!step) {
      this.helpers.set([])
      return
    }

    const metadata =
      step.metadata && typeof step.metadata === 'object'
        ? (step.metadata as Record<string, unknown>)
        : null
    const helpers = this.normalizeHelperArray(metadata?.['helpers'])
    this.helpers.set(helpers)

    const stepKey = step.slug ?? step.id
    const session = this.sessionDetails()
    if (!stepKey || !session) {
      return
    }

    const sessionHelperChats = session.helper_chats ?? {}
    for (const helper of helpers) {
      const helperKey = this.buildHelperKey(stepKey, helper.id)
      this.helperRegistry.update((c) => ({ ...c, [helperKey]: { ...helper, stepKey } }))
      const chatId = sessionHelperChats[helperKey]
      if (chatId) {
        this.helperChats.update((c) => ({ ...c, [helperKey]: chatId as string }))
        this.ensureHelperChatLoaded(chatId as string)
      }
    }

    this.ensureActiveHelperSelection(stepKey, helpers)
  }

  renderTemplate(template: string, context: Record<string, unknown>): string {
    return renderJinja(template, context)
  }

  getReviewInputValue(stepId: string): unknown {
    const step = this.revisionStepById().get(stepId)
    if (!step || step.step_type !== 'input') {
      return null
    }

    const config = step.config as Record<string, unknown> | null

    const entry = this.historyEntryByStepId().get(stepId)
    if (!entry) {
      return null
    }

    return readSavedInputValue(config, entry.context_snapshot)
  }

  /**
   * The options a completed input step was answered against, resolved from the
   * step-history snapshot.
   *
   * `_push_history` deep-copies the context after the answer is written, so the
   * entry already carries the resolved `options_from` array. Without it the
   * recap can only print raw saved values ("25", "BA"), losing the label and
   * the OK/KO state the operator actually declared.
   */
  getReviewInputOptions(stepId: string): StepOption[] {
    const config = this.localizedInputConfigById().get(stepId)
    if (!config) {
      return []
    }

    const optionsFrom = config['options_from']
    if (typeof optionsFrom === 'string' && optionsFrom) {
      const path = optionsFrom.startsWith('context.') ? optionsFrom.slice(8) : optionsFrom
      const entry = this.historyEntryByStepId().get(stepId)
      if (!entry) {
        return []
      }
      const resolved = this.getValueAtPath(entry.context_snapshot, path)
      return Array.isArray(resolved) ? normalizeStepOptions(resolved) : []
    }

    const options = config['options']
    return Array.isArray(options) ? normalizeStepOptions(options) : []
  }

  sendHelperMessage(message: string) {
    if (!this.canSendHelperMessage()) {
      return
    }

    const content = message.trim()
    if (!content) {
      return
    }

    const chatId = this.activeHelperChatId()
    if (chatId) {
      this.gateway.sendChatMessage({
        chat_id: chatId,
        workflow: 'protocol_helper',
        message: content,
        images: []
      })
      return
    }

    // No chat yet — store the message and open the helper to create one.
    const activeKey = this.activeHelperKey()
    if (!activeKey) {
      return
    }

    const dotIndex = activeKey.lastIndexOf('.')
    const helperId = dotIndex >= 0 ? activeKey.slice(dotIndex + 1) : null
    if (!helperId) {
      return
    }

    this.pendingHelperMessage = content
    this.openHelper(helperId)
  }

  private resetProtocolSubscriptions() {
    this.protocolSubscriptions.unsubscribe()
    this.protocolSubscriptions = new Subscription()
    this.protocolSubscribedFor = null
  }

  private resetChatSubscriptions() {
    this.chatSubscriptions.unsubscribe()
    this.chatSubscriptions = new Subscription()
    this.chatSubscribedFor = null
  }

  private resetHelperSubscriptions() {
    this.helperSubscriptions.unsubscribe()
    this.helperSubscriptions = new Subscription()
    this.helperJoinedChats.clear()
  }

  private markSessionCompleted(session?: ProtocolSession) {
    const wasAlreadyClosed = this.isClosed()
    this.isClosed.set(true)
    // Stop auto-resuming on reconnect: completed sessions resume read-only and
    // there is nothing live left to subscribe to.
    this.socket.setResumePayload('protocol', null)
    if (session) {
      this.sessionDetails.set(session)
    } else {
      this.sessionDetails.update((current) => {
        if (!current || current.status === 'completed') {
          return current
        }
        return { ...current, status: 'completed' }
      })
    }

    if (!wasAlreadyClosed) {
      // Seed review outputs from the in-memory cache (populated during the live session).
      const cached: Record<string, ReviewAgentData> = {}
      for (const [key, entry] of this.agentOutputCache) {
        cached[key] = {
          output: entry.output,
          thoughts: entry.thoughts,
          sources: entry.sources,
          toolCalls: entry.toolCalls,
          messageId: entry.messageId,
          messageStatus: 'done'
        }
      }
      if (Object.keys(cached).length) {
        this.reviewAgentOutputs.set(cached)
      }

      this.loadAllStepChatOutputs()
    }
  }

  private maybeOpenFeedbackDialog() {
    if (this.feedbackDialogOpen) {
      return
    }

    const step = this.localizedCurrentStep()
    if (!shouldOpenFeedbackDialog(step, this.sessionDetails())) {
      this.completeSessionAfterFeedback()
      return
    }

    const config =
      step?.config && typeof step.config === 'object' ? (step.config as WorkflowEndConfig) : null

    const sessionId = this.sessionId()
    if (!config || !sessionId) {
      this.completeSessionAfterFeedback()
      return
    }

    this.feedbackDialogOpen = true
    this.openSessionFeedbackDialog({
      sessionId,
      prompt: config.feedback_prompt,
      ratingEnabled: config.feedback_rating_enabled ?? true,
      commentEnabled: config.feedback_comment_enabled ?? true,
      questions: config.feedback_questions
    })
  }

  /** Mid-session free-text note — does not close or advance the session. */
  openStepFeedbackDialog() {
    const sessionId = this.sessionId()
    const stepSlug = this.currentStep()?.slug
    if (!sessionId || this.isClosed()) {
      return
    }

    const dialogRef = this.dialogService.open(StepFeedbackDialogComponent, {
      context: { sessionId, stepSlug: stepSlug || undefined, $hideCloseButton: true },
      contentClass: '!w-[92vw] !max-w-3xl',
      disableClose: true,
      closeOnBackdropClick: false,
      closeOnOutsidePointerEvents: false
    })

    dialogRef.closed$.subscribe((result?: ProtocolSession | null) => {
      if (result && typeof result === 'object' && 'id' in result) {
        this.sessionDetails.set(result)
        this.toast.success(this.translate.instant('step_mode.toasts.feedback_submitted'))
      }
    })
  }

  private openSessionFeedbackDialog(context: SessionFeedbackDialogContext) {
    // The end-of-protocol questionnaire is the widest feedback form we render:
    // long choice labels and revealed follow-ups were cramped in the 568px
    // panel other feedback dialogs use. Cap at the workspace's `3xl` dialog
    // width (768px) and let it shrink with the viewport, so a tablet or phone
    // still gets a usable panel rather than a fixed pixel width it cannot hold.
    const dialogRef = this.dialogService.open(SessionFeedbackDialogComponent, {
      context: { ...context, $hideCloseButton: true },
      contentClass: '!w-[92vw] !max-w-3xl',
      disableClose: true,
      closeOnBackdropClick: false,
      closeOnOutsidePointerEvents: false
    })

    dialogRef.closed$.subscribe((result?: boolean | ProtocolSession) => {
      this.feedbackDialogOpen = false
      const submitted = !!result && typeof result === 'object' && 'id' in result
      if (submitted) {
        this.sessionDetails.set(result)
        this.toast.success(this.translate.instant('step_mode.toasts.feedback_submitted'))
      }

      const pending = this.pendingExit
      this.pendingExit = null
      if (pending) {
        // Dialog closed → now fire the exit transition (compute + END).
        this.feedbackAlreadyToasted = submitted
        this.completeWhenEndArrives = true
        this.gateway.transition(pending.trigger, pending.value)
        this.isLoading.set(true)
        return
      }

      // Already on END (abandoned session): complete without another transition.
      this.completeSessionAfterFeedback({ feedbackAlreadyToasted: submitted })
    })
  }

  private completeSessionAfterFeedback(opts: { feedbackAlreadyToasted?: boolean } = {}) {
    const sessionId = this.sessionId()
    if (!sessionId || this.isClosed()) {
      return
    }

    this.sessionsService.completeSession(sessionId).subscribe({
      next: (session) => {
        this.markSessionCompleted(session)
        if (!opts.feedbackAlreadyToasted) {
          this.toast.success(this.translate.instant('step_mode.toasts.protocol_completed'))
        }
      },
      error: () => {
        this.toast.error(this.translate.instant('step_mode.errors.protocol_error'))
      }
    })
  }

  private applyProtocolNameOverride() {
    const protocolName = this.protocolNameOverride()
    if (!protocolName) {
      return
    }

    this.sessionDetails.update((current) => {
      if (!current) {
        return current
      }

      const context = { ...(current.context ?? {}) }
      context['protocol_name'] = protocolName
      return {
        ...current,
        context
      }
    })

    this.localContext.update((current) => ({
      ...current,
      protocol_name: protocolName
    }))
  }

  private applyProtocolNameFromCache(sessionId: string) {
    const cached = this.gateway.getCachedSessionName(sessionId)
    if (!cached) {
      return
    }

    this.setProtocolNameOverride(this.localizedProtocolName(cached))
  }

  /** The protocol name in the current language, English canonical otherwise. */
  private localizedProtocolName(source: ProtocolNameSource): string {
    return localizeStep(source, this.localeService.currentLang()).name
  }

  startNewSession() {
    const protocolSlug = this.sessionDetails()?.protocol_slug
    if (!protocolSlug) {
      this.toast.error(this.translate.instant('step_mode.errors.start_session_failed'))
      return of(null)
    }

    this.isLoading.set(true)
    this.isClosed.set(false)

    const session = this.sessionDetails()
    const revision = session?.revision
    const payload: ProtocolSessionCreatePayload = {
      protocol_slug: protocolSlug
    }
    if (typeof revision === 'object' && !revision.is_published) {
      payload.revision_version = revision.version
    }
    if (session?.entity_id) {
      payload.entity_id = session.entity_id
    }

    return this.sessionsService.createSession(payload).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((err) => {
        console.error('Failed to start new session', err)
        this.isLoading.set(false)
        this.toast.error(this.translate.instant('step_mode.errors.start_session_failed'))
        return of(null)
      })
    )
  }

  private subscribeChatForStep(step: ProtocolStepPayload) {
    if (step.step_type !== 'ai') {
      this.resetChatSubscriptions()
      return
    }

    const chatId =
      step.config && typeof step.config === 'object' && typeof step.config['chat_id'] === 'string'
        ? (step.config['chat_id'] as string)
        : null

    if (!chatId) {
      return
    }

    // Restore existing agent output (e.g. when going back).
    this.restoreAgentOutput(step, chatId)

    if (this.chatSubscribedFor === chatId) {
      return
    }

    this.resetChatSubscriptions()
    this.chatSubscribedFor = chatId
    this.gateway.joinChatRoom(chatId)

    this.chatSubscriptions.add(
      this.gateway.listenChatMessages().subscribe((event) => {
        if (!event || event.chat_id !== chatId) {
          return
        }

        const message = event.message
        if (!message) {
          return
        }

        // Handle tool-role messages
        if (message.role === 'tool') {
          this.agentToolCalls.update((tools) => {
            const idx = tools.findIndex((t) => t.id === message.id)
            if (idx >= 0) {
              const updated = [...tools]
              updated[idx] = message
              return updated
            }
            return [...tools, message]
          })
          return
        }

        if (message.role !== 'assistant') {
          return
        }

        this.resetAgentTimeout()

        const text = this.getTextFromMessage(message)
        if (text) {
          this.agentOutput.set(text)
        }

        this.agentMessageId.set(message.id)
        this.agentThoughts.set(message.thoughts ?? '')
        this.agentSources.set(message.sources ?? [])

        if (message.status === 'error' && message.error_message) {
          this.agentError.set(message.error_message)
          this.clearAgentTimeout()
        }
      })
    )

    this.chatSubscriptions.add(
      this.gateway.listenChatAgentEvents().subscribe((event: AgentEvent) => {
        if (!event || event.chat_id !== chatId) {
          return
        }

        this.resetAgentTimeout()
        this.updateOutputFromMessages(event.messages)

        switch (event.event_name) {
          case 'AgentStartEvent':
            this.isAgentStreaming.set(true)
            this.agentError.set(null)
            this.agentErrorReason.set(null)
            this.agentErrorDomainType.set(null)
            this.agentOutput.set('')
            this.agentThoughts.set('')
            this.agentSources.set([])
            this.agentToolCalls.set([])
            this.agentMessageId.set('')
            return
          case 'AgentUpdateEvent':
            this.isAgentStreaming.set(true)
            return
          case 'AgentEndEvent':
            this.isAgentStreaming.set(false)
            this.clearAgentTimeout()
            return
          case 'AgentErrorEvent':
            this.isAgentStreaming.set(false)
            this.agentError.set(event.error_message ?? 'Agent error')
            this.agentErrorReason.set(event.reason ?? null)
            this.agentErrorDomainType.set(event.domain_type ?? null)
            this.clearAgentTimeout()
            return
          default:
            return
        }
      })
    )
  }

  private updateStepHistory(step: ProtocolStepPayload) {
    // Snapshot the previous step with the context as-of when it was active;
    // don't retroactively bake in compute-step outputs that only exist after
    // the transition.
    const result = updateStepHistory({
      currentStepId: step.id,
      lastStepId: this.lastStepId,
      history: this.stepHistory(),
      localContext: this.localContext()
    })

    this.stepHistory.set(result.history)
    this.lastStepId = result.lastStepId

    if (result.restoredContext) {
      this.localContext.set(result.restoredContext)
    } else if (step.context) {
      // Forward transition: sync to the authoritative post-compute context so
      // templates render against fresh data.
      this.localContext.set(step.context)
    }
  }

  private restoreAgentOutput(step: ProtocolStepPayload, chatId: string) {
    // Try the local cache first (populated when leaving an AI step). A cached
    // entry whose chatId differs from the step's current chatId means the
    // backend re-launched the agent in a fresh chat (e.g. on back navigation),
    // so the cached output is stale and must be dropped instead of restored.
    const key = step.slug ?? step.id
    if (key) {
      const cached = this.agentOutputCache.get(key)
      if (cached) {
        if (cached.chatId && cached.chatId === chatId) {
          this.agentOutput.set(cached.output)
          this.agentThoughts.set(cached.thoughts)
          this.agentSources.set(cached.sources)
          this.agentToolCalls.set(cached.toolCalls)
          this.agentMessageId.set(cached.messageId)
          this.clearAgentTimeout()
          return
        }
        this.agentOutputCache.delete(key)
      }
    }

    // Fall back to loading from the chat messages API.
    if (this.isAgentStreaming()) {
      return
    }

    this.chatsService.getMessages(chatId).subscribe({
      next: (messages) => {
        if (this.isAgentStreaming()) {
          return
        }

        const assistantMsg = [...messages].reverse().find((m) => m.role === 'assistant')
        if (!assistantMsg) {
          return
        }

        const text = this.getTextFromMessage(assistantMsg)
        if (text) {
          this.agentOutput.set(text)
          this.clearAgentTimeout()
        }

        this.agentMessageId.set(assistantMsg.id)
        this.agentThoughts.set(assistantMsg.thoughts ?? '')
        this.agentSources.set(assistantMsg.sources ?? [])

        const toolMessages = messages.filter((m) => m.role === 'tool')
        if (toolMessages.length) {
          this.agentToolCalls.set(toolMessages)
        }
      },
      error: () => {
        // Chat may not exist yet — ignore silently.
      }
    })
  }

  private resetAgentState(step: ProtocolStepPayload) {
    if (step.step_type !== 'ai') {
      this.agentOutput.set('')
      this.agentThoughts.set('')
      this.agentSources.set([])
      this.agentToolCalls.set([])
      this.agentMessageId.set('')
      this.agentError.set(null)
      this.isAgentStreaming.set(false)
      this.clearAgentTimeout()
      return
    }

    this.agentOutput.set('')
    this.agentThoughts.set('')
    this.agentSources.set([])
    this.agentToolCalls.set([])
    this.agentMessageId.set('')
    this.agentError.set(null)
    this.isAgentStreaming.set(false)
    this.startAgentTimeout()
  }

  private cacheAgentOutput() {
    const step = this.currentStep()
    const output = this.agentOutput()
    if (!step || step.step_type !== 'ai' || !output) {
      return
    }

    const key = step.slug ?? step.id
    if (key) {
      this.agentOutputCache.set(key, {
        output,
        thoughts: this.agentThoughts(),
        sources: this.agentSources(),
        toolCalls: this.agentToolCalls(),
        messageId: this.agentMessageId(),
        chatId: this.chatSubscribedFor
      })
    }
  }

  private startAgentTimeout() {
    this.clearAgentTimeout()
    this.agentTimeoutId = setTimeout(() => {
      this.agentTimeoutId = null
      if (this.currentStep()?.step_type === 'ai' && !this.agentOutput() && !this.agentError()) {
        this.agentError.set('The agent is not responding. Please try again or go back.')
        this.isAgentStreaming.set(false)
      }
    }, StepModeFacadeService.AGENT_TIMEOUT_MS)
  }

  private resetAgentTimeout() {
    if (this.agentTimeoutId !== null) {
      this.startAgentTimeout()
    }
  }

  private clearAgentTimeout() {
    if (this.agentTimeoutId !== null) {
      clearTimeout(this.agentTimeoutId)
      this.agentTimeoutId = null
    }
  }

  private updateHelpersForStep(step: ProtocolStepPayload) {
    const helpers = this.normalizeHelpers(step)
    this.helpers.set(helpers)

    const stepKey = this.resolveHelperStepKey(step)
    if (stepKey) {
      this.helperRegistry.update((current) => {
        const next = { ...current }
        for (const helper of helpers) {
          const helperKey = this.buildHelperKey(stepKey, helper.id)
          next[helperKey] = { ...helper, stepKey }
        }
        return next
      })
    }

    if (!stepKey || helpers.length === 0) {
      this.ensureActiveHelperSelection()
      return
    }

    const helperChats = helpers
      .filter((helper) => typeof helper.chat_id === 'string' && helper.chat_id)
      .map((helper) => ({ key: this.buildHelperKey(stepKey, helper.id), chatId: helper.chat_id! }))

    if (helperChats.length) {
      this.helperChats.update((current) => {
        const next = { ...current }
        for (const item of helperChats) {
          next[item.key] = item.chatId
        }
        return next
      })
    }

    this.ensureActiveHelperSelection(stepKey, helpers)
  }

  private handleHelperOpened(event: ProtocolHelperOpenedEvent | null | undefined) {
    if (!event?.helper_id || !event.chat_id) {
      return
    }

    const step = this.currentStep()
    const stepKey = step ? this.resolveHelperStepKey(step) : null
    if (!stepKey) {
      return
    }

    const helperKey = this.buildHelperKey(stepKey, event.helper_id)
    this.activeHelperKey.set(helperKey)

    this.helperChats.update((current) => ({
      ...current,
      [helperKey]: event.chat_id
    }))

    this.helpers.update((helpers) =>
      helpers.map((helper) =>
        helper.id === event.helper_id ? { ...helper, chat_id: event.chat_id } : helper
      )
    )

    this.ensureHelperChatLoaded(event.chat_id)

    // Flush pending message that triggered the chat creation.
    const pending = this.pendingHelperMessage
    if (pending) {
      this.pendingHelperMessage = null
      this.gateway.sendChatMessage({
        chat_id: event.chat_id,
        workflow: 'protocol_helper',
        message: pending,
        images: []
      })
    }
  }

  private normalizeHelpers(step: ProtocolStepPayload): ProtocolHelperItem[] {
    const rawHelpers = Array.isArray(step.helpers)
      ? step.helpers
      : (step.metadata?.['helpers'] as unknown)

    return this.normalizeHelperArray(rawHelpers)
  }

  private normalizeHelperArray(rawHelpers: unknown): ProtocolHelperItem[] {
    if (!Array.isArray(rawHelpers)) {
      return []
    }

    const helpers = rawHelpers.map((helper): ProtocolHelperItem | null => {
      if (!helper || typeof helper !== 'object') {
        return null
      }

      const record = helper as Record<string, unknown>
      const id = typeof record['id'] === 'string' ? record['id'] : ''
      if (!id) {
        return null
      }

      const available = record['available']
      return {
        id,
        label: typeof record['label'] === 'string' ? record['label'] : undefined,
        icon: typeof record['icon'] === 'string' ? record['icon'] : undefined,
        chat_id: typeof record['chat_id'] === 'string' ? record['chat_id'] : null,
        available: typeof available === 'boolean' ? available : true,
        unavailable_reason:
          typeof record['unavailable_reason'] === 'string' ? record['unavailable_reason'] : null
      }
    })

    return helpers.filter((helper): helper is ProtocolHelperItem => helper !== null)
  }

  private initHelperSubscriptions() {
    this.helperSubscriptions.add(
      this.gateway.listenChatMessages().subscribe((event) => {
        this.handleHelperChatMessage(event)
      })
    )

    this.helperSubscriptions.add(
      this.gateway.listenChatAgentEvents().subscribe((event) => {
        this.handleHelperAgentEvent(event)
      })
    )
  }

  private handleHelperChatMessage(event: NewMessage | null | undefined) {
    if (!event || !this.isHelperChatId(event.chat_id)) {
      return
    }

    this.chatStore.addMessage(event.chat_id, event.message)
  }

  private handleHelperAgentEvent(event: AgentEvent | null | undefined) {
    if (!event || !this.isHelperChatId(event.chat_id)) {
      return
    }

    // AgentErrorEvent: surface as a synthetic assistant error message so the
    // helper UI renders it via the existing assistant-message error path.
    // Carries structured `reason` + `domain_type` so the renderer can pick a
    // localized message (see issue #1337).
    if (event.event_name === 'AgentErrorEvent') {
      const syntheticErrorMessage: ChatMessage = {
        id: `agent-error-${event.run_id || crypto.randomUUID()}`,
        chat_id: event.chat_id,
        role: 'assistant',
        visible: true,
        attachments: null,
        create_date: new Date().toISOString(),
        sent_at: new Date().toISOString(),
        content: [],
        thoughts: null,
        status: 'error',
        error_message: event.error_message ?? null,
        error_reason: event.reason ?? null,
        error_domain_type: event.domain_type ?? null,
        downvote_details: null,
        downvote_reason: null,
        is_good_message: null,
        tool_name: null,
        metadata: null
      }
      this.chatStore.addMessage(event.chat_id, syntheticErrorMessage)
      return
    }

    if (!event.messages || !event.messages.length) {
      return
    }

    for (const message of event.messages) {
      this.chatStore.addMessage(event.chat_id, message)
    }
  }

  private ensureActiveHelperSelection(stepKey?: string | null, helpers?: ProtocolHelperItem[]) {
    if (!stepKey || !helpers || !helpers.length) {
      return
    }

    const prefix = `${stepKey}.`

    // If the current active key belongs to this step, keep it.
    const activeKey = this.activeHelperKey()
    if (activeKey && activeKey.startsWith(prefix)) {
      const chatId = this.helperChats()[activeKey]
      if (chatId) {
        this.ensureHelperChatLoaded(chatId)
      }
      return
    }

    // Select the first helper on this step that already has a chat.
    const helperWithChat = helpers.find((helper) => helper.chat_id)
    if (helperWithChat) {
      const helperKey = this.buildHelperKey(stepKey, helperWithChat.id)
      this.activeHelperKey.set(helperKey)
      const chatId = this.helperChats()[helperKey]
      if (chatId) {
        this.ensureHelperChatLoaded(chatId)
      }
      return
    }

    // Default to the first helper on this step (no chat yet — created lazily).
    this.activeHelperKey.set(this.buildHelperKey(stepKey, helpers[0].id))
  }

  private ensureHelperChatLoaded(chatId: string) {
    if (!chatId) {
      return
    }

    if (!this.helperJoinedChats.has(chatId)) {
      this.helperJoinedChats.add(chatId)
      this.gateway.joinChatRoom(chatId)
    }

    const status = this.helperChatStatus()[chatId] ?? 'idle'
    if (status === 'loading' || status === 'ready') {
      return
    }

    this.setHelperChatStatus(chatId, 'loading')
    this.chatsService.getMessages(chatId).subscribe({
      next: (messages) => {
        this.chatStore.setMessages(chatId, messages)
        this.setHelperChatStatus(chatId, 'ready')
      },
      error: () => {
        this.setHelperChatStatus(chatId, 'error')
      }
    })
  }

  private setHelperChatStatus(chatId: string, status: HelperChatStatus) {
    this.helperChatStatus.update((current) => ({
      ...current,
      [chatId]: status
    }))
  }

  private isHelperChatId(chatId: string): boolean {
    if (!chatId) {
      return false
    }

    return Object.values(this.helperChats()).includes(chatId)
  }

  private isActiveHelperForCurrentStep(): boolean {
    const step = this.currentStep()
    const stepKey = step ? this.resolveHelperStepKey(step) : null
    const activeKey = this.activeHelperKey()

    if (!stepKey || !activeKey) {
      return false
    }

    return this.helpers().some((helper) => this.buildHelperKey(stepKey, helper.id) === activeKey)
  }

  private resolveHelperStepKey(step: ProtocolStepPayload): string | null {
    const slug = step.slug
    if (typeof slug === 'string' && slug) {
      return slug
    }

    const id = step.id
    if (typeof id === 'string' && id) {
      return id
    }

    return null
  }

  private buildHelperKey(stepKey: string, helperId: string): string {
    return `${stepKey}.${helperId}`
  }

  private updateOutputFromMessages(messages?: ChatMessage[]) {
    if (!messages || !messages.length) {
      return
    }

    const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant')
    if (!lastAssistant || !Array.isArray(lastAssistant.content)) {
      return
    }

    const text = this.getTextFromMessage(lastAssistant)
    if (text) {
      this.agentOutput.set(text)
    }

    this.agentMessageId.set(lastAssistant.id)
    this.agentThoughts.set(lastAssistant.thoughts ?? '')
    this.agentSources.set(lastAssistant.sources ?? [])

    const toolMessages = messages.filter((m) => m.role === 'tool')
    if (toolMessages.length) {
      this.agentToolCalls.set(toolMessages)
    }
  }

  private getTextFromMessage(message: ChatMessage): string {
    if (typeof message.content === 'string') {
      return message.content
    }

    if (!Array.isArray(message.content)) {
      return ''
    }

    return message.content
      .filter((part): part is { type: 'text'; text: string } => part?.type === 'text')
      .map((part) => part.text ?? '')
      .join('\n')
  }

  private getValueAtPath(source: Record<string, unknown>, path: string): unknown {
    if (!path) {
      return undefined
    }

    return path.split('.').reduce<unknown>((acc, key) => {
      if (acc == null) {
        return undefined
      }

      if (typeof acc !== 'object') {
        return undefined
      }

      return (acc as Record<string, unknown>)[key]
    }, source)
  }
}
